import {cn} from '@tempmee/core'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {Label} from '../label'
import {useMenuColor} from './color'

export interface MenuHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Avatar element (e.g. `<Avatar />`). */
  avatar?: ReactNode
  /** Short chip rendered below the avatar (e.g. "ADMIN"). */
  avatarBadge?: string
  /** Primary title text. */
  title: string
  /** Secondary subtitle / address text. */
  subtitle?: string
  /** Optional top-right action (e.g. a switch button). */
  action?: ReactNode
}

/**
 * Profile/identity header (extracted from the former `Sidebar.Header`).
 * Used at the top of the `Sidebar` rail, or standalone.
 *
 * Inherits the rail's surface tone: inside a `Sidebar color="dark"` the title,
 * subtitle, and rule invert to white so the header reads on the brand teal.
 */
export const MenuHeader = forwardRef<HTMLDivElement, MenuHeaderProps>(
  ({avatar, avatarBadge, title, subtitle, action, className, ...props}, ref) => {
    const dark = useMenuColor() === 'dark'
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start gap-3 border-b pb-6 pt-10',
          dark ? 'border-white/24' : 'border-divider',
          className,
        )}
        {...props}
      >
        {avatar && (
          <div className="relative shrink-0 pb-2">
            {avatar}
            {avatarBadge && (
              <div className="absolute -bottom-0 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <Label severity="brand" variant="filled">
                  {avatarBadge}
                </Label>
              </div>
            )}
          </div>
        )}

        <div className="min-w-0 flex-1 pt-1">
          <p
            className={cn(
              'truncate text-[18px] font-bold leading-snug',
              dark ? 'text-white' : 'text-text-primary',
            )}
          >
            {title}
          </p>
          {subtitle && (
            <p
              className={cn(
                'mt-0.5 text-[15px] font-normal leading-snug',
                dark ? 'text-white/70' : 'text-text-secondary',
              )}
            >
              {subtitle}
            </p>
          )}
        </div>

        {action && <div className="shrink-0 pt-1">{action}</div>}
      </div>
    )
  },
)

MenuHeader.displayName = 'MenuHeader'
